import {useMemo} from 'react'
import type {SidebarChat} from '../types/sidebar-chat'
import type {UITask} from '../types/task'
import {NO_REPOSITORY_GROUP} from '../constants/grouping'
import {ALL_STATUS_OPTIONS, OPEN_STATUSES, matchesDetailedStatus} from '../utils/filter-utils'
import {getSidebarChatFilterStatus} from '../utils/sidebar-chat-filters'
import {sortTasksByDate} from '../utils/task-helpers'
import {
  getSessionEnvironmentBucket,
  SESSION_ENVIRONMENT_BUCKET_LABELS,
  SESSION_ENVIRONMENT_BUCKET_ORDER,
} from '../utils/environment-bucket'

export type TaskRepoGroup = readonly [nwo: string, tasks: UITask[]]

export type GroupableItem = {kind: 'task'; task: UITask} | {kind: 'chat'; chat: SidebarChat}

export type SidebarGroup = readonly [key: string, items: GroupableItem[]]

const NO_STATUS_GROUP = 'Unknown'

function itemUpdatedAtDate(item: GroupableItem): Date {
  const dateString = item.kind === 'task' ? item.task.latest_session_updated_at : item.chat.updatedAt
  return new Date(dateString)
}

function itemSortTimestamp(item: GroupableItem): number {
  const timestamp = itemUpdatedAtDate(item).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function sortGroupableItemsByDate(items: readonly GroupableItem[]): GroupableItem[] {
  return [...items].sort((a, b) => itemSortTimestamp(b) - itemSortTimestamp(a))
}

// Display order, match order, and labels for status groups are all derived from
// ALL_STATUS_OPTIONS (the single shared list of detailed statuses in filter-utils.ts), so
// adding a status there automatically gets a section here. Tasks matching none of these
// fall into NO_STATUS_GROUP.
const STATUS_MATCH_ORDER: readonly string[] = ALL_STATUS_OPTIONS.map(option => option.value)
const STATUS_GROUP_LABELS = new Map(ALL_STATUS_OPTIONS.map(option => [option.value, option.label]))

/**
 * Matches a status candidate for grouping purposes, with one local exception:
 * `matchesDetailedStatus` alone can't distinguish an archived task from a merely completed
 * one (archived_at is independent of session/artifact state), so an archived task would
 * otherwise match both 'archived' and 'completed'. Since 'completed' comes before 'archived'
 * in ALL_STATUS_OPTIONS' display order, exclude the open statuses (in-progress, queued,
 * idle, needs-attention, failed, completed, cancelled, timed_out) here so archived tasks
 * always land under "Archived" rather than their underlying session-state label.
 */
function matchesStatusForGrouping(task: UITask, status: string): boolean {
  if (task.archived_at && OPEN_STATUSES.has(status)) return false
  return matchesDetailedStatus(task, status)
}

/**
 * Pure grouping helper. Buckets tasks by their `nwo` ("owner/repo") and
 * preserves the input order — both for the order in which repos first appear
 * and for the tasks within each repo. Tasks without an `nwo` are grouped
 * together under `NO_REPOSITORY_GROUP`, which is always sorted to the bottom.
 */
export function groupTasksByRepo(tasks: readonly UITask[]): TaskRepoGroup[] {
  const groups = new Map<string, UITask[]>()
  for (const task of tasks) {
    const key = task.nwo ?? NO_REPOSITORY_GROUP
    const existing = groups.get(key)
    if (existing) {
      existing.push(task)
    } else {
      groups.set(key, [task])
    }
  }
  // Surface the repo-less group at the bottom, preserving the input order of the rest.
  const orphanGroup = groups.get(NO_REPOSITORY_GROUP)
  const entries = Array.from(groups.entries()).filter(([key]) => key !== NO_REPOSITORY_GROUP)
  if (orphanGroup) {
    entries.push([NO_REPOSITORY_GROUP, orphanGroup])
  }
  return entries
}

export function useGroupedTasks(tasks: readonly UITask[]): TaskRepoGroup[] {
  return useMemo(() => groupTasksByRepo(tasks), [tasks])
}

const TODAY_GROUP = 'Today'
const YESTERDAY_GROUP = 'Yesterday'
const OLDER_GROUP = 'Older'
const DAY_MS = 24 * 60 * 60 * 1000
// Dated buckets ("Aug 25") cover days 2-6 back; combined with Today (0) and Yesterday (1)
// that's a 7-day window before everything falls into Older.
const MAX_DATED_BUCKET_DAYS_AGO = 6

function startOfLocalDay(date: Date): Date {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric'}).format(date)
}

function bucketByRecencyDay<T>(items: readonly T[], getDate: (item: T) => Date, now: Date): Array<[string, T[]]> {
  const startOfToday = startOfLocalDay(now)
  const buckets = new Map<string, {sortKey: number; items: T[]}>()

  for (const item of items) {
    const updatedAt = getDate(item)
    const daysAgo = Number.isNaN(updatedAt.getTime())
      ? Number.POSITIVE_INFINITY
      : Math.round((startOfToday.getTime() - startOfLocalDay(updatedAt).getTime()) / DAY_MS)

    let label: string
    if (daysAgo <= 0) label = TODAY_GROUP
    else if (daysAgo === 1) label = YESTERDAY_GROUP
    else if (daysAgo <= MAX_DATED_BUCKET_DAYS_AGO) label = formatShortDate(updatedAt)
    else label = OLDER_GROUP

    const sortKey = daysAgo <= 0 ? 0 : daysAgo
    const existing = buckets.get(label)
    if (existing) {
      existing.items.push(item)
    } else {
      buckets.set(label, {sortKey, items: [item]})
    }
  }

  return [...buckets.entries()]
    .sort(([, a], [, b]) => a.sortKey - b.sortKey)
    .map(([label, bucket]) => [label, bucket.items] as [string, T[]])
}

/**
 * Buckets tasks by the local calendar day of `latest_session_updated_at`: "Today",
 * "Yesterday", then "Mon DD" for the next 5 days, then "Older" for everything beyond
 * that 7-day window (or with an unparseable timestamp). Used for the "Last updated"
 * sidebar grouping mode. Flattening multiple repo groups can interleave their
 * individually-sorted task lists, so tasks are re-sorted by date (newest first)
 * before bucketing to keep each bucket's contents in global recency order.
 */
export function groupTasksByRecency(groups: readonly TaskRepoGroup[], now: Date = new Date()): TaskRepoGroup[] {
  const allTasks = sortTasksByDate(groups.flatMap(([, tasks]) => tasks))
  return bucketByRecencyDay(allTasks, task => new Date(task.latest_session_updated_at), now)
}

function taskStatusLabel(task: UITask): string {
  const status = STATUS_MATCH_ORDER.find(candidate => matchesStatusForGrouping(task, candidate))
  return status ? (STATUS_GROUP_LABELS.get(status) ?? NO_STATUS_GROUP) : NO_STATUS_GROUP
}

function chatStatusLabel(chat: SidebarChat): string {
  const status = chat.legacy ? undefined : getSidebarChatFilterStatus(chat.latestSessionState)
  return status ? (STATUS_GROUP_LABELS.get(status) ?? NO_STATUS_GROUP) : NO_STATUS_GROUP
}

export function groupTasksByStatus(groups: readonly TaskRepoGroup[]): TaskRepoGroup[] {
  const allTasks = groups.flatMap(([, tasks]) => tasks)
  const buckets = new Map<string, UITask[]>()

  for (const task of allTasks) {
    const label = taskStatusLabel(task)
    const existing = buckets.get(label)
    if (existing) {
      existing.push(task)
    } else {
      buckets.set(label, [task])
    }
  }

  const orderedLabels = [...ALL_STATUS_OPTIONS.map(option => option.label), NO_STATUS_GROUP]
  return orderedLabels
    .filter(label => buckets.has(label))
    .map(label => {
      const groupTasks = buckets.get(label)
      return [label, groupTasks ?? []] as TaskRepoGroup
    })
}

function addGroupableItem(buckets: Map<string, GroupableItem[]>, label: string, item: GroupableItem): void {
  const existing = buckets.get(label)
  if (existing) {
    existing.push(item)
  } else {
    buckets.set(label, [item])
  }
}

export function chatsWithoutMatchingTasks(tasks: readonly UITask[], chats: readonly SidebarChat[]): SidebarChat[] {
  const taskIds = new Set(tasks.map(task => task.id))
  return chats.filter(chat => chat.legacy || !taskIds.has(chat.taskId))
}

function uniqueTasksAcrossGroups(groups: readonly TaskRepoGroup[]): UITask[] {
  const seen = new Set<string>()
  return groups.flatMap(([, tasks]) =>
    tasks.filter(task => {
      if (task.id && seen.has(task.id)) return false
      if (task.id) seen.add(task.id)
      return true
    }),
  )
}

export function groupSessionsByStatus(
  groups: readonly TaskRepoGroup[],
  chats: readonly SidebarChat[] = [],
): SidebarGroup[] {
  const buckets = new Map<string, GroupableItem[]>()
  const tasks = uniqueTasksAcrossGroups(groups)

  for (const task of tasks) {
    addGroupableItem(buckets, taskStatusLabel(task), {kind: 'task', task})
  }

  for (const chat of chatsWithoutMatchingTasks(tasks, chats)) {
    addGroupableItem(buckets, chatStatusLabel(chat), {kind: 'chat', chat})
  }

  const orderedLabels = [...ALL_STATUS_OPTIONS.map(option => option.label), NO_STATUS_GROUP]
  return orderedLabels
    .filter(label => buckets.has(label))
    .map(label => [label, sortGroupableItemsByDate(buckets.get(label) ?? [])] as SidebarGroup)
}

export function groupSessionsByEnvironment(
  groups: readonly TaskRepoGroup[],
  chats: readonly SidebarChat[] = [],
): SidebarGroup[] {
  const buckets = new Map<string, GroupableItem[]>()
  const tasks = uniqueTasksAcrossGroups(groups)

  for (const task of tasks) {
    addGroupableItem(buckets, getSessionEnvironmentBucket(task.current_environment?.kind, task.agentSlug), {
      kind: 'task',
      task,
    })
  }

  for (const chat of chatsWithoutMatchingTasks(tasks, chats)) {
    addGroupableItem(buckets, getSessionEnvironmentBucket(chat.environment?.kind, chat.agentSlug), {kind: 'chat', chat})
  }

  return SESSION_ENVIRONMENT_BUCKET_ORDER.filter(bucket => buckets.has(bucket)).map(
    bucket =>
      [SESSION_ENVIRONMENT_BUCKET_LABELS[bucket], sortGroupableItemsByDate(buckets.get(bucket) ?? [])] as SidebarGroup,
  )
}

export function groupSessionsByRecency(
  groups: readonly TaskRepoGroup[],
  chats: readonly SidebarChat[] = [],
  now: Date = new Date(),
): SidebarGroup[] {
  const tasks = uniqueTasksAcrossGroups(groups)
  const taskItems: GroupableItem[] = tasks.map(task => ({kind: 'task', task}))
  const chatItems: GroupableItem[] = chatsWithoutMatchingTasks(tasks, chats).map(chat => ({kind: 'chat', chat}))
  const allItems = sortGroupableItemsByDate([...taskItems, ...chatItems])
  return bucketByRecencyDay(allItems, itemUpdatedAtDate, now)
}
